import { css } from "styled-components";

/**
 * Shared fragments for the hds primitives. Every value is a token — if you need
 * a number that is not here, add it to the spec first.
 */

/**
 * DESIGN_SYSTEM.md §9: "Focus rings are not optional. An interface built from 1px
 * #E4E4E4 borders has almost no intrinsic affordance without them."
 *
 * The ring is the secondary purple, not the primary lime — lime is 2.70:1 on
 * white and a focus indicator has to clear 3:1. See §1.
 */
export const focusRing = css`
	&:focus-visible {
		outline: var(--focus-ring);
		outline-offset: var(--focus-ring-offset);
	}
`;

/**
 * §9: a control's own caption is never selectable.
 *
 * Nobody copies the word on a button, but everybody drags across one — a
 * double-click on a toggle, a slightly-moved click on a tool — and the caption
 * highlighting blue in response is the interface answering a question that was
 * not asked. Real prose (an object name, a description, an article number) is
 * left selectable; this applies only to the text that IS the click target.
 *
 * `user-select` inherits, so declaring it on the control covers the caption and
 * anything nested inside it without needing a rule per element.
 */
export const unselectable = css`
	user-select: none;
	-webkit-user-select: none;
`;

/** §9: disabled fades, it never inverts. */
export const disabledState = css`
	&:disabled,
	&[aria-disabled="true"] {
		opacity: 0.45;
		cursor: not-allowed;
	}
`;

/** §9: transitions are 120ms ease-out, and only on colour properties. */
export const colorTransition = css`
	transition: background-color var(--transition-base), border-color var(--transition-base),
		color var(--transition-base);
`;

/** The 1px border every component outline uses. §4: never 2px, never a shadow. */
export const outlined = css`
	border: var(--border-default);
	border-radius: var(--radius-md);
	background: var(--color-surface);
	box-shadow: none;
`;

/** Body / control label type. §2. */
export const bodyType = css`
	font-family: var(--font-family);
	font-size: var(--font-size-body);
	font-weight: var(--font-weight-regular);
	line-height: var(--line-height-base);
	color: var(--color-text);
`;

/** Helper / caption type. §2. */
export const captionType = css`
	font-family: var(--font-family);
	font-size: var(--font-size-caption);
	font-weight: var(--font-weight-regular);
	line-height: var(--line-height-tight);
	color: var(--color-text-secondary);
`;

/** A 24px icon slot that never shrinks in a flex row. §6. */
export const iconSlot = css`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: var(--size-icon);
	height: var(--size-icon);

	svg {
		width: 100%;
		height: 100%;
		stroke-width: var(--size-icon-stroke);
	}
`;
